import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { firestore } from '@/lib/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { Content, Conversation, IntakeSpec } from '@shared/types';
import { useSendContentMutation } from '@/services/messageService';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import { IntakeForm } from '@/components/intake/IntakeForm';
import {
  ImageStartCard,
  type MeshKickoff,
} from '@/components/intake/ImageStartCard';
import { TextStartCard } from '@/components/intake/TextStartCard';
import { Switch } from '@/components/ui/switch';
import { SHOWCASE_PRESETS } from '@/components/intake/presets';
import { intakeToSeedSpec } from '@/lib/intakeToSeedSpec';
import { humanReadableSummary } from '@/lib/humanReadableSummary';
import { specParametersToArtifactParameters } from '@/lib/specParametersToArtifactParameters';
import { stripUndefined } from '@/utils/stripUndefined';
import {
  DEFAULT_PARAMETRIC_MODEL,
  DEFAULT_PARAMETRIC_MODEL_SUPPORTS_THINKING,
} from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/contexts/LocaleContext';

export function PromptView() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useTranslation(['prompt', 'common']);
  const { locale } = useLocale();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isBuildingFromImage, setIsBuildingFromImage] = useState(false);
  const [isBuildingFromText, setIsBuildingFromText] = useState(false);
  // Mesh-mode toggle: OFF = text intake (TextStartCard) is active; ON =
  // image-to-3D intake (ImageStartCard) is active. Both cards always
  // render — the inactive one gets a disabled overlay so the layout
  // stays stable. Default OFF since the parametric path is the primary
  // one (mesh outputs are sculpted, not parametrically editable).
  const [meshMode, setMeshMode] = useState(false);

  const newConversationId = useMemo(() => crypto.randomUUID(), []);

  const { mutateAsync: sendMessageAsync } = useSendContentMutation({
    conversation: {
      id: newConversationId,
      user_id: user?.id ?? '',
      current_message_leaf_id: null,
    },
  });

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsLoaded(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return t('morning', { ns: 'prompt' });
    if (hour < 18) return t('afternoon', { ns: 'prompt' });
    return t('evening', { ns: 'prompt' });
  }, [t]);

  const userName = user?.user_metadata?.name || user?.user_metadata?.full_name;

  const handleSubmit = async (intake: IntakeSpec) => {
    if (!user?.id) {
      toast({
        title: t('loginRequiredTitle', { ns: 'prompt' }),
        description: t('loginRequiredDescription', { ns: 'prompt' }),
        variant: 'destructive',
      });
      return;
    }
    try {
      const seedSpec = intakeToSeedSpec(intake);
      const now = new Date().toISOString();
      const conversation = {
        id: newConversationId,
        user_id: user.id,
        title: t('newConversationTitle', { ns: 'prompt' }),
        created_at: now,
        updated_at: now,
        current_message_leaf_id: null,
        category: intake.category,
        intake,
        locale,
      };

      await setDoc(doc(firestore, 'conversations', newConversationId), stripUndefined(conversation));

      queryClient.setQueryData<Conversation[]>(
        ['conversations', 'recent'],
        (old) => {
          if (!old) return [conversation as Conversation];
          return [conversation as Conversation, ...old];
        },
      );

      const text =
        humanReadableSummary(intake, locale) +
        (intake.freeform ? '\n\n' + intake.freeform : '');

      const initialContent: Content = {
        text,
        artifact: {
          title: seedSpec.title,
          version: '1',
          code: '',
          parameters: specParametersToArtifactParameters(seedSpec.parameters),
          files: [],
          spec: seedSpec,
        },
      };

      sendMessageAsync(initialContent).catch((err) => {
        console.error('Failed to send initial message:', err);
      });

      navigate(`/editor/${newConversationId}`);
    } catch (err) {
      console.error(err);
      toast({
        title: t('loginRequiredTitle', { ns: 'prompt' }),
        description: err instanceof Error ? err.message : t('generationFailed', { ns: 'prompt' }),
        variant: 'destructive',
      });
    }
  };

  const handlePresetSubmit = (preset: (typeof SHOWCASE_PRESETS)[number]) => {
    void handleSubmit({
      ...preset.intake,
      core: {
        ...preset.intake.core,
        intendedUse: t(`examples.${preset.key}.intendedUse`, { ns: 'prompt' }),
      },
    });
  };

  /** Image-first path: ImageStartCard pre-creates a draft conversation,
   *  uploads the image, and kicks off a Tripo mesh job. We just navigate
   *  to /editor/:id — the editor subscribes to live progress and renders
   *  the mesh once it's ready. No intake-form, no Gemini text seed, no
   *  artifact write. */
  const handleBuildFromMesh = async ({ conversationId }: MeshKickoff) => {
    if (isBuildingFromImage) return;
    setIsBuildingFromImage(true);
    try {
      navigate(`/editor/${conversationId}`);
    } finally {
      setIsBuildingFromImage(false);
    }
  };

  /** Text-only path: no intake form, no image — just send the user's
   *  freeform description as the first chat message. The chat function
   *  builds an empty DesignSpec and auto-detects the category from the
   *  message content (categoryPrompts.autoDetectCategory), so the agent
   *  has everything it needs to start. */
  const handleBuildFromText = async (text: string) => {
    if (!user?.id) {
      toast({
        title: t('loginRequiredTitle', { ns: 'prompt' }),
        description: t('loginRequiredDescription', { ns: 'prompt' }),
        variant: 'destructive',
      });
      return;
    }
    if (isBuildingFromText) return;
    setIsBuildingFromText(true);
    try {
      const now = new Date().toISOString();
      const conversation = {
        id: newConversationId,
        user_id: user.id,
        title:
          text.split('.').slice(0, 1).join('.').slice(0, 80) ||
          t('newConversationTitle', { ns: 'prompt' }),
        created_at: now,
        updated_at: now,
        current_message_leaf_id: null,
        locale,
      };
      await setDoc(
        doc(firestore, 'conversations', newConversationId),
        stripUndefined(conversation),
      );

      queryClient.setQueryData<Conversation[]>(
        ['conversations', 'recent'],
        (old) => {
          if (!old) return [conversation as Conversation];
          return [conversation as Conversation, ...old];
        },
      );

      const initialContent: Content = {
        text,
        model: DEFAULT_PARAMETRIC_MODEL,
        thinking: DEFAULT_PARAMETRIC_MODEL_SUPPORTS_THINKING,
      };
      sendMessageAsync(initialContent).catch((err) => {
        console.error('Failed to send initial text-build message:', err);
      });
      navigate(`/editor/${newConversationId}`);
    } catch (err) {
      console.error('[handleBuildFromText] failed:', err);
      toast({
        title: t('generationFailed', {
          ns: 'prompt',
          defaultValue: 'Could not start design',
        }),
        description: err instanceof Error ? err.message : '',
        variant: 'destructive',
      });
      setIsBuildingFromText(false);
    }
  };

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-k-canvas">
      {/* Decorative flat blocks (no gradients) */}
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-brand-teal-soft/30" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand-teal/20" />

      <main className="relative z-10 mx-auto flex h-full w-full max-w-3xl flex-col overflow-y-auto px-4 pb-6 pt-6 sm:px-6 sm:pt-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="mb-5"
        >
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-brand-indigo sm:text-4xl">
            {greeting}
            {userName && <span className="text-brand-indigo/60">, {userName}</span>}
          </h1>
          <p className="mt-2 text-sm text-k-ink/80 sm:text-base">
            {t('subtitlePrefix', { ns: 'prompt' })}
            <span className="font-bold text-brand-indigo">
              {t('subtitleDrawing', { ns: 'prompt' })}
            </span>
            {t('subtitleMiddle', { ns: 'prompt' })}
            <span className="font-bold text-brand-indigo">
              {t('subtitleModel', { ns: 'prompt' })}
            </span>
          </p>
        </motion.div>

        {/* Quick-start preset chips */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          className="mb-5 flex flex-wrap items-center gap-2"
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-indigo/60">
            {t('quickExamples', { ns: 'prompt' })}
          </span>
          {SHOWCASE_PRESETS.map((preset) => {
            const Icon = preset.icon;
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => handlePresetSubmit(preset)}
                className="inline-flex items-center gap-1.5 rounded-full border-2 border-brand-indigo/30 bg-k-card px-3 py-1.5 text-xs font-bold text-brand-indigo transition-colors hover:border-brand-indigo hover:bg-brand-teal hover:text-brand-indigo"
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{t(`examples.${preset.key}.label`, { ns: 'prompt' })}</span>
              </button>
            );
          })}
        </motion.div>

        {/* Mesh-mode toggle: gates which of the two start-cards (text or
            image-to-3D) is active. Both cards still render at all times
            so the layout doesn't jump; the inactive one gets a disabled
            overlay with a hint pointing the user at this switch. */}
        {user?.id && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.07, ease: [0.16, 1, 0.3, 1] }}
            className="mb-3 flex w-full items-center gap-3"
          >
            <Switch
              id="mesh-mode-toggle"
              checked={meshMode}
              onCheckedChange={setMeshMode}
              aria-label={t('meshModeToggle.label', {
                ns: 'prompt',
                defaultValue: 'Mesh mode (image-to-3D)',
              })}
            />
            <label
              htmlFor="mesh-mode-toggle"
              className="cursor-pointer select-none text-xs font-bold text-brand-indigo"
            >
              {t('meshModeToggle.label', {
                ns: 'prompt',
                defaultValue: 'Mesh mode (image-to-3D)',
              })}
            </label>
          </motion.div>
        )}

        {/* Image-first entry: drop a reference photo, click "Generate
            3D mesh" → straight into the editor and Tripo runs in the
            background. Disabled (overlay) when mesh-mode is OFF. */}
        {user?.id && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="relative mb-4 w-full"
          >
            <div className={meshMode ? '' : 'pointer-events-none opacity-50'}>
              <ImageStartCard
                conversationId={newConversationId}
                userId={user.id}
                onMeshKickedOff={handleBuildFromMesh}
                isBuilding={isBuildingFromImage}
                disabled={!meshMode}
              />
            </div>
            {!meshMode && (
              <div className="pointer-events-none absolute inset-0 flex items-start justify-center pt-2">
                <span className="rounded-full bg-k-card px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-indigo shadow ring-1 ring-brand-indigo/20">
                  {t('meshModeToggle.imageHint', {
                    ns: 'prompt',
                    defaultValue: 'Turn on Mesh mode to upload an image.',
                  })}
                </span>
              </div>
            )}
          </motion.div>
        )}

        {/* Text-only entry: type a sentence or two, click Build, jump
            straight into the editor. Disabled (overlay) when mesh-mode
            is ON. */}
        {user?.id && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.09, ease: [0.16, 1, 0.3, 1] }}
            className="relative mb-4 w-full"
          >
            <div className={meshMode ? 'pointer-events-none opacity-50' : ''}>
              <TextStartCard
                onBuild={handleBuildFromText}
                isBuilding={isBuildingFromText}
              />
            </div>
            {meshMode && (
              <div className="pointer-events-none absolute inset-0 flex items-start justify-center pt-2">
                <span className="rounded-full bg-k-card px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-indigo shadow ring-1 ring-brand-indigo/20">
                  {t('meshModeToggle.textHint', {
                    ns: 'prompt',
                    defaultValue: 'Turn off Mesh mode to use text intake.',
                  })}
                </span>
              </div>
            )}
          </motion.div>
        )}

        {/* The form */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="w-full"
        >
          <IntakeForm onSubmit={handleSubmit} />
        </motion.div>
      </main>
    </div>
  );
}
